// @ai-generated 2026-08-27T00:00:00+00:00 by matthias.gessinger@synqony.com using Claude Opus 5 (1M context)
import { useAuftragRepository } from '@Hooks/useRepository';
import { type ApiInvoiceSnapshot } from '@Model';
import { OrderSnapshotsRepository } from '@Repositories/Auftragsbearbeitung';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

/** A requisition an invoice snapshot can navigate to, labelled by the order that was billed. */
export interface LinkedRequisition {
    requisitionId: string;
    orderNumber: string;
}

/** One billed order snapshot, resolved to the requisition it belongs to. */
interface ResolvedOrder extends LinkedRequisition {
    orderSnapshotId: string;
}

/**
 * The requisitions behind an invoice snapshot. Its positions point at the order snapshots they were
 * billed from, and only those carry the requisition id, so each distinct one is loaded to resolve it.
 * Several positions usually share one order, and one requisition can hold several orders — both are
 * collapsed into {@link LinkedRequisition}s, so that result is the distinct set of requisitions this
 * invoice touches. `requisitionByOrderSnapshot` keeps the unmerged view, which is what a single
 * position needs to find its own requisition.
 */
export function useInvoiceSnapshotRequisitions(snapshot: ApiInvoiceSnapshot, enabled: boolean) {
    const orderSnapshots = useAuftragRepository(OrderSnapshotsRepository);

    const orderSnapshotIds = useMemo(
        () => [
            ...new Set(
                snapshot.positions
                    .map((position) => position.order.foreign_key)
                    .filter((id): id is string => id !== undefined && id !== ''),
            ),
        ],
        [snapshot.positions],
    );

    const { data, isLoading } = useQuery({
        queryFn: async () => {
            const results = await Promise.all(orderSnapshotIds.map((id) => orderSnapshots.get(id)));

            // Paired with the requested id rather than the payload's, so a position finds its order
            // even if the backend ever answers with a differently keyed snapshot.
            const resolved: ResolvedOrder[] = [];
            results.forEach((result, index) => {
                const order = result?.data;
                if (order?.requisition_id === undefined || order.requisition_id === '') {
                    return;
                }
                resolved.push({
                    orderSnapshotId: orderSnapshotIds[index],
                    requisitionId: order.requisition_id,
                    orderNumber: order.number,
                });
            });

            return resolved;
        },
        queryKey: ['order_snapshots', 'requisitions', orderSnapshotIds],
        enabled: enabled && orderSnapshotIds.length > 0,
    });

    const orders = useMemo(() => data ?? [], [data]);

    const requisitions = useMemo(() => {
        const byRequisition = new Map<string, LinkedRequisition>();
        for (const order of orders) {
            if (!byRequisition.has(order.requisitionId)) {
                byRequisition.set(order.requisitionId, {
                    requisitionId: order.requisitionId,
                    orderNumber: order.orderNumber,
                });
            }
        }
        return [...byRequisition.values()];
    }, [orders]);

    const requisitionByOrderSnapshot = useMemo(
        () => new Map(orders.map((order) => [order.orderSnapshotId, order.requisitionId])),
        [orders],
    );

    return { requisitions, requisitionByOrderSnapshot, isLoading };
}
